import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export const AvailabilityHelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white">
        <div className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Setting Your Availability</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="mb-3 text-lg font-medium text-gray-900">
                How to Set Your Availability
              </h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  Use this page to let coaches know when you're available for training sessions.
                  This helps them create optimal schedules that work for everyone.
                </p>

                <div className="rounded-lg bg-blue-50 p-4">
                  <h4 className="mb-2 font-medium text-blue-900">Quick Tips:</h4>
                  <ul className="space-y-1 text-sm text-blue-800">
                    <li>
                      • <strong>Desktop:</strong> Click individual time slots or use preset buttons
                    </li>
                    <li>
                      • <strong>Mobile:</strong> Use day tabs and quick add buttons
                    </li>
                    <li>
                      • Set your <span className="font-medium text-green-600">preferred times</span>{' '}
                      when you most want to train
                    </li>
                    <li>
                      • Mark yourself <span className="font-medium text-blue-600">available</span>{' '}
                      on other moments of the day
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="mb-3 text-lg font-medium text-gray-900">Mobile Interface</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  <strong>Day Tabs:</strong> Swipe or tap to switch between days of the week.
                </p>
                <p>
                  <strong>Quick Add Buttons:</strong> Use preset time slots for common training
                  times:
                </p>
                <ul className="ml-4 space-y-1 text-sm">
                  <li>
                    • <strong>Early Morning:</strong> 09:00 - 09:00
                  </li>
                  <li>
                    • <strong>Morning:</strong> 09:00 - 11:00
                  </li>
                  <li>
                    • <strong>Before Lunch:</strong> 11:00 - 13:00
                  </li>
                  <li>
                    • <strong>After Lunch:</strong> 13:00 - 15:00
                  </li>
                  <li>
                    • <strong>Afternoon:</strong> 15:00 - 17:00
                  </li>
                  <li>
                    • <strong>Evening:</strong> 17:00 - 20:00
                  </li>
                </ul>
                <p>
                  <strong>Custom Times:</strong> Use the "Add" button to set specific time ranges.
                </p>
              </div>
            </div>

            <div>
              <h3 className="mb-3 text-lg font-medium text-gray-900">Desktop Interface</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  <strong>Grid View:</strong> See your entire week at a glance with 30-minute time
                  slots.
                </p>
                <p>
                  <strong>Quick Add Buttons:</strong> Use preset time slots for common training
                  times:
                </p>
                <ul className="ml-4 space-y-1 text-sm">
                  <li>
                    • <strong>All</strong> The entire day
                  </li>
                  <li>
                    • <strong>07</strong> 07:00 - 09:00
                  </li>
                  <li>
                    • <strong>09</strong> 09:00 - 11:00
                  </li>
                  <li>
                    • <strong>11</strong> 11:00 - 13:00
                  </li>
                  <li>
                    • <strong>13</strong> 13:00 - 15:00
                  </li>
                  <li>
                    • <strong>15</strong> 15:00 - 17:00
                  </li>
                  <li>
                    • <strong>17</strong> 17:00 - 20:00
                  </li>
                </ul>
                <p>
                  <strong>Custom Times:</strong> Click and drag to select a time range.
                </p>
                <p>
                  <strong>Preferred</strong> Upgrade the slot from available to preference by
                  clicking it.
                </p>
                <ul className="ml-4 space-y-1 text-sm"></ul>
                <p>
                  <strong>Daily Quick Add:</strong> Each day has quick buttons for common time
                  periods.
                </p>
              </div>
            </div>

            <div>
              <h3 className="mb-3 text-lg font-medium text-gray-900">📅 Calendar Integration</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  <strong>Calendar Events:</strong> View and manage your external calendar events
                  alongside your availability to avoid conflicts and optimize your training
                  schedule.
                </p>

                <div className="rounded-lg bg-blue-50 p-4">
                  <h4 className="mb-2 font-medium text-blue-900">Calendar Features:</h4>
                  <ul className="space-y-1 text-sm text-blue-800">
                    <li>
                      • <strong>Timeline View:</strong> Visual timeline showing your day with events
                      and availability
                    </li>
                    <li>
                      • <strong>Event Display:</strong> See all calendar events with times and
                      durations
                    </li>
                    <li>
                      • <strong>Free Time Detection:</strong> Automatically identify available time
                      gaps
                    </li>
                    <li>
                      • <strong>Quick Actions:</strong> Add availability around events or fill free
                      gaps
                    </li>
                    <li>
                      • <strong>Tap to Add:</strong> Click on the timeline to add 30-minute
                      availability slots
                    </li>
                  </ul>
                </div>

                <div className="rounded-lg bg-green-50 p-4">
                  <h4 className="mb-2 font-medium text-green-900">Smart Suggestions:</h4>
                  <ul className="space-y-1 text-sm text-green-800">
                    <li>
                      • <strong>Available Gaps:</strong> System suggests optimal training times
                      between events
                    </li>
                    <li>
                      • <strong>One-Click Fill:</strong> "Make day available except events" button
                    </li>
                    <li>
                      • <strong>Buffer Times:</strong> Add availability before/after events with
                      30-minute buffers
                    </li>
                    <li>
                      • <strong>Conflict Prevention:</strong> Avoid scheduling conflicts with
                      existing commitments
                    </li>
                  </ul>
                </div>

                <p>
                  <strong>Calendar Toggle:</strong> Enable/disable calendar view using the toggle
                  switch in the header. When enabled, you'll see your events and can interact with
                  the timeline.
                </p>
              </div>
            </div>

            <div>
              <h3 className="mb-3 text-lg font-medium text-gray-900">👥 Crew-mates Availability</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  <strong>Crew-mates Overlay:</strong> See when your training group members are
                  available to help coordinate optimal training times and crew formations.
                </p>

                <div className="rounded-lg bg-purple-50 p-4">
                  <h4 className="mb-2 font-medium text-purple-900">Crew-mates Features:</h4>
                  <ul className="space-y-1 text-sm text-purple-800">
                    <li>
                      • <strong>Availability Heatmap:</strong> Color-coded overview of crew
                      availability by hour
                    </li>
                    <li>
                      • <strong>Merged Time Blocks:</strong> See consolidated availability periods
                      with participant counts
                    </li>
                    <li>
                      • <strong>Crew Filtering:</strong> Filter by specific crews to focus on
                      relevant groups
                    </li>
                    <li>
                      • <strong>Real-time Counts:</strong> View how many people are available at
                      each time slot
                    </li>
                    <li>
                      • <strong>Preferred vs Available:</strong> Distinguish between preferred and
                      general availability
                    </li>
                  </ul>
                </div>

                <div className="rounded-lg bg-yellow-50 p-4">
                  <h4 className="mb-2 font-medium text-yellow-900">Color Coding:</h4>
                  <div className="grid grid-cols-1 gap-2 text-sm text-yellow-800 md:grid-cols-2">
                    <div className="flex items-center space-x-2">
                      <div className="h-3 w-3 rounded bg-green-500"></div>
                      <span>
                        <strong>Green:</strong> 80%+ crew available
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="h-3 w-3 rounded bg-green-400"></div>
                      <span>
                        <strong>Light Green:</strong> 60-79% available
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="h-3 w-3 rounded bg-yellow-400"></div>
                      <span>
                        <strong>Yellow:</strong> 40-59% available
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="h-3 w-3 rounded bg-orange-400"></div>
                      <span>
                        <strong>Orange:</strong> 20-39% available
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="h-3 w-3 rounded bg-red-400"></div>
                      <span>
                        <strong>Red:</strong> &lt;20% available
                      </span>
                    </div>
                  </div>
                </div>

                <p>
                  <strong>Crew-mates Toggle:</strong> Enable/disable crew-mates view using the
                  toggle switch in the header. When enabled, you'll see availability patterns and
                  can filter by specific crews.
                </p>

                <p>
                  <strong>Daily Summary:</strong> View the top 5 busiest times and merged
                  availability blocks for the selected day to quickly identify optimal training
                  windows.
                </p>
              </div>
            </div>

            <div>
              <h3 className="mb-3 text-lg font-medium text-gray-900">💡 Advanced Tips</h3>
              <div className="space-y-3 text-gray-700">
                <div className="rounded-lg bg-gray-50 p-4">
                  <h4 className="mb-2 font-medium text-gray-900">Combined Features:</h4>
                  <ul className="space-y-1 text-sm text-gray-800">
                    <li>
                      • <strong>Calendar + Crew-mates:</strong> Use both features together to find
                      optimal training times
                    </li>
                    <li>
                      • <strong>Timeline Interaction:</strong> Tap the timeline to quickly add
                      availability in free slots
                    </li>
                    <li>
                      • <strong>Filter Strategy:</strong> Start with all crews, then filter to
                      specific groups as needed
                    </li>
                    <li>
                      • <strong>Availability Patterns:</strong> Look for green blocks in the heatmap
                      for best crew turnout
                    </li>
                    <li>
                      • <strong>Event Planning:</strong> Schedule around calendar events and crew
                      availability peaks
                    </li>
                  </ul>
                </div>

                <div className="rounded-lg bg-indigo-50 p-4">
                  <h4 className="mb-2 font-medium text-indigo-900">Mobile Optimization:</h4>
                  <ul className="space-y-1 text-sm text-indigo-800">
                    <li>
                      • <strong>Touch-Friendly:</strong> All interactions optimized for mobile
                      devices
                    </li>
                    <li>
                      • <strong>Compact View:</strong> Information condensed for smaller screens
                    </li>
                    <li>
                      • <strong>Quick Actions:</strong> One-tap buttons for common availability
                      patterns
                    </li>
                    <li>
                      • <strong>Responsive Design:</strong> Adapts to different screen sizes and
                      orientations
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TrainingSessionsHelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white">
        <div className="sticky top-0 flex items-center justify-between border-b bg-white p-6">
          <h2 className="text-2xl font-bold text-gray-900">How to Use Training Sessions</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-8 p-6">
          {/* Overview Section */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">📋 Overview</h3>
            <p className="mb-4 text-gray-700">
              The Training Sessions page is the page where you can see all rowing training
              activities. Create, organize, schedule, and track training sessions with crew
              assignments and smart scheduling options.
            </p>
            <p className="text-gray-700">
              <b>Note:</b> Read through the entire instruction to understand how the hierarchical
              session structure works. This is crucial for effective session management.
            </p>
          </section>

          {/* Session Types */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">🔄 Session Types & Status</h3>
            <div className="space-y-4">
              <div className="rounded-lg bg-yellow-50 p-4">
                <h4 className="mb-2 font-semibold text-yellow-900">Unscheduled Sessions</h4>
                <p className="text-yellow-800">
                  Newly created sessions without specific times but with a date. These appear in the
                  planning view for scheduling. You can schedule these sessions by dragging them to
                  the calendar and then selecting the crews you want to assign to this time.
                </p>
              </div>
              <div className="rounded-lg bg-blue-50 p-4">
                <h4 className="mb-2 font-semibold text-blue-900">Scheduled Sessions</h4>
                <p className="text-blue-800">
                  Sessions with assigned dates, times, and crew configurations ready for training.
                </p>
              </div>
              <div className="rounded-lg bg-orange-50 p-4">
                <h4 className="mb-2 font-semibold text-orange-900">Partially Scheduled Sessions</h4>
                <p className="text-orange-800">
                  Parent sessions where some crews have been scheduled at specific times, but others
                  remain unscheduled.
                </p>
              </div>
              <div className="rounded-lg bg-green-50 p-4">
                <h4 className="mb-2 font-semibold text-green-900">Completed Sessions</h4>
                <p className="text-green-800">
                  Sessions where all assigned crews have been scheduled across different time slots.
                </p>
              </div>
            </div>
          </section>

          {/* Creating Sessions */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">
              ➕ Creating Training Sessions
            </h3>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-400 bg-blue-50 p-4">
                <h4 className="mb-2 font-semibold text-blue-800">Step 1: Basic Information</h4>
                <ul className="list-inside list-disc space-y-1 text-blue-700">
                  <li>
                    <strong>Title:</strong> Descriptive name for the training session
                  </li>
                  <li>
                    <strong>Description:</strong> Optional details about the session content
                  </li>
                  <li>
                    <strong>Date:</strong> Target training date
                  </li>
                  <li>
                    <strong>Duration:</strong> Session length in minutes (default: 90)
                  </li>
                  <li>
                    <strong>Location:</strong> Training venue or water location (optional)
                  </li>
                </ul>
              </div>
              <div className="border-l-4 border-green-400 bg-green-50 p-4">
                <h4 className="mb-2 font-semibold text-green-800">Step 2: Crew Assignments</h4>
                <p className="mb-2 text-green-700">
                  Add crews to the session using the dropdown selector. Each crew assignment creates
                  a connection that enables scheduling flexibility through session splitting. You
                  will be able to schedule a session for multiple crews at different times also
                  assigning different coaches to each session. You can always add more crews to the
                  parent session. Then the session becomes available for scheduling in the planner.
                </p>
                <ul className="list-inside list-disc space-y-1 text-green-700">
                  <li>Select crews from the dropdown menu</li>
                  <li>Multiple crews can be assigned to the same session</li>
                  <li>Remove crews using the × button if needed</li>
                  <li>
                    Leave empty for "Open Sessions" available to any crew, you will not be able to
                    schedule this session using the planner
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Bulk import sessions */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">#️⃣ Bulk Import Sessions</h3>
            <div className="space-y-4">
              <div className="border-l-4 border-purple-400 bg-purple-50 p-4">
                <h4 className="mb-2 font-semibold text-purple-800">Importing Sessions</h4>
                <p className="mb-2 text-purple-700">
                  You can import multiple training sessions at once using the Import Sessions
                  button. This is useful for adding large numbers of sessions quickly when you have
                  a schedule ready.
                </p>
              </div>
            </div>
          </section>

          {/* Session Management */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">⚙️ Session Management</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <span className="text-2xl">✏️</span>
                <div>
                  <h4 className="font-semibold">Edit Sessions</h4>
                  <p className="text-gray-700">
                    Modify session details, crew assignments, or timing. Changes to parent sessions
                    update all related child sessions.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-2xl">📅</span>
                <div>
                  <h4 className="font-semibold">Schedule Sessions</h4>
                  <p className="text-gray-700">
                    Assign specific times and fine-tune crew selections. Access the full scheduling
                    interface from here.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-2xl">👁️</span>
                <div>
                  <h4 className="font-semibold">View Details</h4>
                  <p className="text-gray-700">
                    See complete session information including all crew assignments and timing
                    details.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-2xl">🔄</span>
                <div>
                  <h4 className="font-semibold">Reschedule</h4>
                  <p className="text-gray-700">
                    Move scheduled sessions back to unscheduled status for re-planning.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-2xl">🗑️</span>
                <div>
                  <h4 className="font-semibold">Delete Sessions</h4>
                  <p className="text-gray-700">
                    Remove individual sessions or entire session groups with confirmation prompts.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Hierarchical View */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">
              🌳 Hierarchical Session View
            </h3>
            <p className="mb-4 text-gray-700">
              Sessions are organized in a parent-child hierarchy that makes session splitting and
              management intuitive.
            </p>

            <div className="space-y-4">
              <div className="border-l-4 border-blue-400 bg-blue-50 p-4">
                <h4 className="mb-2 font-semibold text-blue-800">Parent Sessions</h4>
                <p className="mb-2 text-blue-700">
                  Original sessions that can be expanded to show their scheduled variants (child
                  sessions).
                </p>
                <ul className="list-inside list-disc space-y-1 text-blue-700">
                  <li>Click the expand/collapse icon to view child sessions</li>
                  <li>Show combined crew information and scheduling status</li>
                  <li>Display "Split" badges when partially scheduled</li>
                  <li>Use "Expand All" / "Collapse All" buttons for bulk operations</li>
                </ul>
              </div>

              <div className="border-l-4 border-green-400 bg-green-50 p-4">
                <h4 className="mb-2 font-semibold text-green-800">
                  Child Sessions (Split Instances)
                </h4>
                <p className="mb-2 text-green-700">
                  Scheduled variants of parent sessions with specific times and crew selections.
                </p>
                <ul className="list-inside list-disc space-y-1 text-green-700">
                  <li>Indented under their parent session for clarity</li>
                  <li>Show specific scheduling times and assigned crews</li>
                  <li>Can be edited independently for time adjustments</li>
                  <li>Maintain connection to parent for coordinated updates</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Session Splitting Workflow */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">
              ✂️ Session Splitting Workflow
            </h3>
            <div className="space-y-4">
              <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
                <h4 className="mb-2 font-semibold text-yellow-800">Why Split Sessions?</h4>
                <p className="text-yellow-700">
                  Schedule the same training content for different crews at different times while
                  maintaining organizational clarity and allowing for crew-specific timing
                  preferences.
                </p>
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <h4 className="mb-2 font-semibold text-gray-800">Example Workflow:</h4>
                <ol className="list-inside list-decimal space-y-2 text-gray-700">
                  <li>
                    <strong>Create:</strong> "Endurance Training" session assigned to Senior 8+,
                    Junior 8+, and Club 4x
                  </li>
                  <li>
                    <strong>Schedule:</strong> Senior 8+ and Junior 8+ for 6:00 AM using the
                    scheduling modal
                  </li>
                  <li>
                    <strong>Result:</strong> Child session created at 6:00 AM; parent session
                    retains Club 4x
                  </li>
                  <li>
                    <strong>Continue:</strong> Schedule Club 4x for 8:00 AM separately
                  </li>
                  <li>
                    <strong>Final:</strong> Two scheduled child sessions + completed parent session
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* Mobile Interface */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">📱 Mobile Interface</h3>
            <p className="mb-4 text-gray-700">
              On mobile devices, sessions display as expandable cards with all the same
              functionality optimized for touch interaction.
            </p>
            <div className="space-y-2">
              <p className="text-gray-700">• Tap cards to expand and view details</p>
              <p className="text-gray-700">• Access all management actions through card buttons</p>
              <p className="text-gray-700">• Hierarchical relationships preserved in card layout</p>
              <p className="text-gray-700">• Optimized button sizes for touch interaction</p>
            </div>
          </section>

          {/* Permissions */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">🔐 Role-Based Permissions</h3>
            <div className="space-y-3">
              <div className="rounded bg-blue-50 p-3">
                <h4 className="font-semibold text-blue-900">Coaches & Admins</h4>
                <p className="text-sm text-blue-800">
                  Full access to create, edit, schedule, and delete training sessions.
                </p>
              </div>
              <div className="rounded bg-gray-50 p-3">
                <h4 className="font-semibold text-gray-900">Rowers</h4>
                <p className="text-sm text-gray-800">
                  View-only access to see training sessions and their details.
                </p>
              </div>
            </div>
          </section>

          {/* Tips */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">💡 Pro Tips</h3>
            <div className="space-y-2 text-gray-700">
              <p>• Use descriptive titles to easily identify sessions in the planning view</p>
              <p>• Create sessions with multiple crews for flexible scheduling options</p>
              <p>• Use the statistics panel to track your session management progress</p>
              <p>• Expand/collapse sessions strategically to focus on relevant information</p>
              <p>• Check child sessions before deleting parent sessions to avoid data loss</p>
              <p>• Use the scheduling interface from here to access advanced planning features</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export const PlannerHelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white">
        <div className="sticky top-0 flex items-center justify-between border-b bg-white p-6">
          <h2 className="text-2xl font-bold text-gray-900">How to Use the Training Planner</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-8 p-6">
          {/* Overview Section */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">📋 Overview</h3>
            <p className="mb-4 text-gray-700">
              The Training Planner is your central hub for scheduling rowing sessions. It provides a
              comprehensive view of unscheduled sessions, crew availability, and already scheduled
              activities for efficient training management.
            </p>
          </section>

          {/* Page Layout */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">🖥️ Page Layout</h3>
            <div className="space-y-4">
              <div className="rounded-lg bg-blue-50 p-4">
                <h4 className="mb-2 font-semibold text-blue-900">
                  Top Panel: Unscheduled Sessions
                </h4>
                <p className="text-blue-800">
                  Shows all training sessions that haven't been scheduled yet. These are draggable
                  and ready to be assigned to time slots.
                </p>
              </div>
              <div className="rounded-lg bg-green-50 p-4">
                <h4 className="mb-2 font-semibold text-green-900">
                  Middle Panel: Availability Grid
                </h4>
                <p className="text-green-800">
                  The main scheduling interface showing time slots and crew availability. This is
                  where you drop sessions to schedule them.
                </p>
              </div>
              <div className="rounded-lg bg-purple-50 p-4">
                <h4 className="mb-2 font-semibold text-purple-900">
                  Bottom Panel: Scheduled Sessions
                </h4>
                <p className="text-purple-800">
                  Displays all sessions that have already been scheduled for the selected day.
                </p>
              </div>
            </div>
          </section>

          {/* Navigation and Filtering */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">🗓️ Navigation & Filtering</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <span className="text-2xl">📅</span>
                <div>
                  <h4 className="font-semibold">Date Navigation</h4>
                  <p className="text-gray-700">
                    Use the date picker and arrow buttons (top center) to navigate between days.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-2xl">🚣</span>
                <div>
                  <h4 className="font-semibold">Crew Filtering</h4>
                  <p className="text-gray-700">
                    Filter by specific crews (top right) to focus on particular groups. Shows
                    availability for selected crews only.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Main Workflow */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">
              ⚡ How to Schedule Sessions
            </h3>
            <div className="space-y-4">
              <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4">
                <h4 className="mb-2 font-semibold text-yellow-800">Step 1: Drag & Drop</h4>
                <p className="text-yellow-700">
                  Drag an unscheduled session from the top panel and drop it onto a time slot in the
                  availability grid. The grid shows crew availability with color coding.
                </p>
              </div>
              <div className="border-l-4 border-blue-400 bg-blue-50 p-4">
                <h4 className="mb-2 font-semibold text-blue-800">
                  Step 2: Multi-Step Configuration
                </h4>
                <p className="text-blue-700">
                  A detailed modal opens with 3 steps to configure your session:
                </p>
                <ol className="mt-2 list-inside list-decimal space-y-1 text-blue-700">
                  <li>
                    <strong>Crew & Rower Selection:</strong> Choose crews and assign specific rowers
                    to positions
                  </li>
                  <li>
                    <strong>Coach Assignment:</strong> Select available coaches for the session
                  </li>
                  <li>
                    <strong>Review & Confirm:</strong> Verify all details and resolve any conflicts
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* Availability Grid */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">
              📊 Understanding the Availability Grid
            </h3>
            <div className="space-y-3">
              <h4 className="font-semibold">Color Coding:</h4>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="flex items-center space-x-3">
                  <div className="h-4 w-4 rounded bg-emerald-400"></div>
                  <span>
                    <strong>Emerald:</strong> Preferred time slots
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-4 w-4 rounded bg-teal-400"></div>
                  <span>
                    <strong>Teal:</strong> Mixed preferred and available
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-4 w-4 rounded bg-blue-400"></div>
                  <span>
                    <strong>Blue:</strong> Available time slots
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-4 w-4 rounded bg-gray-300"></div>
                  <span>
                    <strong>Gray:</strong> Not available
                  </span>
                </div>
              </div>
              <div className="mt-4">
                <h5 className="mb-2 text-sm font-medium">Individual User Indicators:</h5>
                <div className="space-y-1 text-sm">
                  <div>★ = Preferred time</div>
                  <div>✓ = Available</div>
                  <div>✗ = Not available</div>
                </div>
              </div>
            </div>
          </section>

          {/* Schedule Modal Details */}
          {/* Session Splitting Section */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900"> Session Splitting</h3>
            <p className="mb-4 text-gray-700">
              The Training Planner supports advanced session splitting, allowing you to schedule the
              same training session for different crews at different times while maintaining
              flexibility for unscheduled crews.
            </p>

            <div className="space-y-4">
              <div className="border-l-4 border-blue-400 bg-blue-50 p-4">
                <h4 className="mb-2 font-semibold text-blue-800">How Session Splitting Works</h4>
                <ol className="list-inside list-decimal space-y-2 text-blue-700">
                  <li>
                    <strong>Initial Session:</strong> Create one unscheduled session assigned to
                    multiple crews
                  </li>
                  <li>
                    <strong>Partial Scheduling:</strong> Select specific crews to schedule at a
                    particular time
                  </li>
                  <li>
                    <strong>Automatic Splitting:</strong> System creates a scheduled session for
                    selected crews
                  </li>
                  <li>
                    <strong>Remaining Session:</strong> Original session persists with remaining
                    unscheduled crews
                  </li>
                  <li>
                    <strong>Repeat Process:</strong> Continue scheduling remaining crews at
                    different times
                  </li>
                </ol>
              </div>

              <div className="rounded-lg bg-yellow-50 p-4">
                <h4 className="mb-2 font-semibold text-yellow-800">Split Session Indicators</h4>
                <ul className="space-y-1 text-yellow-700">
                  <li>
                    🟠 <strong>Split Badge:</strong> Sessions marked with "Split" when partially
                    scheduled
                  </li>
                  <li>
                    📊 <strong>Progress Counter:</strong> Shows "X/Y" crews scheduled vs total crews
                  </li>
                  <li>
                    ⚠️ <strong>Remainder Warning:</strong> Indicates when crews are still
                    unscheduled
                  </li>
                  <li>
                    🔗 <strong>Linked Sessions:</strong> All split instances maintain connection to
                    original session
                  </li>
                </ul>
              </div>

              <div className="rounded-lg bg-green-50 p-4">
                <h4 className="mb-2 font-semibold text-green-800">
                  Example: Water Training Session
                </h4>
                <div className="space-y-2 text-green-700">
                  <p>
                    <strong>Initial:</strong> "Water Training" assigned to Senior 8+, Junior 8+,
                    Club 8+, Novice 8+
                  </p>
                  <p>
                    <strong>First Schedule:</strong> Senior 8+ and Junior 8+ at 6:00 AM
                  </p>
                  <p>
                    <strong>Result:</strong>
                    <br />• Scheduled session at 6:00 AM with 2 crews
                    <br />• Unscheduled session remains with Club 8+ and Novice 8+
                  </p>
                  <p>
                    <strong>Second Schedule:</strong> Club 8+ at 8:00 AM
                  </p>
                  <p>
                    <strong>Final:</strong> Three instances total - two scheduled, one unscheduled
                    (Novice 8+)
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Enhanced Modal Features Section */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">
              🔧 Advanced Schedule Modal Features
            </h3>
            <div className="space-y-4">
              <div>
                <h4 className="mb-2 font-semibold">Step 1: Enhanced Crew & Rower Configuration</h4>
                <div className="space-y-2 rounded bg-gray-50 p-3 text-sm">
                  <p>
                    <strong>Crew Types:</strong>
                  </p>
                  <ul className="ml-4 list-inside list-disc space-y-1">
                    <li>
                      <strong>Standard Crews:</strong> Auto-populated with existing lineups, locked
                      assignments
                    </li>
                    <li>
                      <strong>Experimental Crews:</strong> Full drag-and-drop lineup customization
                    </li>
                  </ul>
                  <p>
                    <strong>Rower Pool Features:</strong>
                  </p>
                  <ul className="ml-4 list-inside list-disc space-y-1">
                    <li>
                      <strong>Availability Toggle:</strong> Switch between "Available Only" and
                      "Show All" views
                    </li>
                    <li>
                      <strong>Detailed Indicators:</strong> ✅ Fully Available, ⚠️ Partly Available,
                      ❌ Not Available, 🚫 Already Assigned
                    </li>
                    <li>
                      <strong>Side Preferences:</strong> 🔴 Port (Strict), 🟢 Starboard (Strict), ⚪
                      Either Side
                    </li>
                    <li>
                      <strong>Cross-Crew Prevention:</strong> Rowers become unavailable once
                      assigned to any crew in session
                    </li>
                  </ul>
                  <p>
                    <strong>Drag-and-Drop Validation:</strong>
                  </p>
                  <ul className="ml-4 list-inside list-disc space-y-1">
                    <li>Real-time seat highlighting during drag operations</li>
                    <li>Side preference warnings for non-preferred assignments</li>
                    <li>Boat balance validation for sweep boats (equal port/starboard)</li>
                    <li>Position requirement checking (cox, stroke positions)</li>
                  </ul>
                </div>
              </div>

              <div>
                <h4 className="mb-2 font-semibold">Step 2: Advanced Coach Management</h4>
                <div className="space-y-2 rounded bg-gray-50 p-3 text-sm">
                  <p>
                    <strong>Coach Availability Views:</strong>
                  </p>
                  <ul className="ml-4 list-inside list-disc space-y-1">
                    <li>
                      <strong>Primary View:</strong> Shows only fully available coaches from
                      training group
                    </li>
                    <li>
                      <strong>Advanced View:</strong> Reveals all coaches with detailed conflict
                      information
                    </li>
                  </ul>
                  <p>
                    <strong>Conflict Management:</strong>
                  </p>
                  <ul className="ml-4 list-inside list-disc space-y-1">
                    <li>
                      ✅ <strong>Fully Available:</strong> No conflicts for entire session duration
                    </li>
                    <li>
                      ⚠️ <strong>Partly Available:</strong> Shows exact time overlap details
                    </li>
                    <li>
                      🔄 <strong>Double-Booking:</strong> Already coaching other crews with detailed
                      session info
                    </li>
                    <li>
                      ❌ <strong>Not Available:</strong> Complete unavailability with reason
                    </li>
                  </ul>
                  <p>
                    <strong>Double-Booking Features:</strong>
                  </p>
                  <ul className="ml-4 list-inside list-disc space-y-1">
                    <li>Clear warnings: "Coach already scheduled for [Session] with [Crew]"</li>
                    <li>Confirmation prompts for intentional double-booking</li>
                    <li>Multiple coach assignment support</li>
                  </ul>
                </div>
              </div>

              <div>
                <h4 className="mb-2 font-semibold">Step 3: Comprehensive Validation & Summary</h4>
                <div className="space-y-2 rounded bg-gray-50 p-3 text-sm">
                  <p>
                    <strong>Validation Severity Levels:</strong>
                  </p>
                  <ul className="ml-4 list-inside list-disc space-y-1">
                    <li>
                      🔴 <strong>Errors:</strong> Must resolve before saving (capacity violations,
                      missing positions)
                    </li>
                    <li>
                      🟡 <strong>Warnings:</strong> Can proceed with acknowledgment (side
                      preferences, coach conflicts)
                    </li>
                    <li>
                      🔵 <strong>Information:</strong> Advisory suggestions (optimization
                      recommendations)
                    </li>
                  </ul>
                  <p>
                    <strong>Conflict Resolution Panel:</strong>
                  </p>
                  <ul className="ml-4 list-inside list-disc space-y-1">
                    <li>Detailed availability impact assessments</li>
                    <li>Side preference mismatch warnings with boat balance analysis</li>
                    <li>Coach double-booking summary with session details</li>
                    <li>Capacity and position requirement validation</li>
                  </ul>
                  <p>
                    <strong>Final Confirmation:</strong>
                  </p>
                  <ul className="ml-4 list-inside list-disc space-y-1">
                    <li>Comprehensive session overview with all participants</li>
                    <li>Warning acknowledgment checkboxes</li>
                    <li>Conflict severity assessment before saving</li>
                    <li>Unsaved changes protection</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Tips and Best Practices */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">💡 Tips & Best Practices</h3>
            <div className="rounded-lg bg-gray-50 p-4">
              <ul className="space-y-2 text-gray-700">
                <li>
                  🎯 <strong>Plan Ahead:</strong> Check availability patterns before scheduling
                </li>
                <li>
                  ⚖️ <strong>Balance Crews:</strong> Ensure equal port/starboard distribution for
                  sweep boats
                </li>
                <li>
                  👥 <strong>Consider Conflicts:</strong> Review partial availability and make
                  informed decisions
                </li>
                <li>
                  🔄 <strong>Use Filters:</strong> Focus on specific crews when planning targeted
                  sessions
                </li>
                <li>
                  ⏰ <strong>Peak Times:</strong> Green time slots typically offer the best
                  scheduling options
                </li>
                <li>
                  📝 <strong>Double-check:</strong> Always review the summary step before confirming
                </li>
                <li>
                  🚀 <strong>Experimental Crews:</strong> Use drag-and-drop for flexible lineup
                  testing
                </li>
                <li>
                  🔄 <strong>Session Splitting:</strong> Schedule large sessions incrementally by
                  crew availability
                </li>
                <li>
                  ⚠️ <strong>Acknowledge Warnings:</strong> Review all conflict warnings before
                  proceeding
                </li>
                <li>
                  👨‍🏫 <strong>Coach Management:</strong> Consider coach workload when allowing
                  double-bookings
                </li>
                <li>
                  🎯 <strong>Side Preferences:</strong> Respect rower preferences but balance boat
                  requirements
                </li>
                <li>
                  📊 <strong>Monitor Progress:</strong> Use split session indicators to track
                  scheduling completion
                </li>
              </ul>
            </div>
          </section>

          {/* Troubleshooting */}
          <section>
            <h3 className="mb-4 text-xl font-semibold text-gray-900">🛠️ Troubleshooting</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold text-red-600">Can't drop a session?</h4>
                <p className="text-gray-700">
                  Make sure you're dropping on a valid time slot and that the session isn't already
                  scheduled.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-red-600">Modal won't open?</h4>
                <p className="text-gray-700">
                  Ensure the session drop was successful and try refreshing the page if issues
                  persist.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-red-600">Availability data missing?</h4>
                <p className="text-gray-700">
                  Crew members may need to update their availability in their personal calendars.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-red-600">Split session not updating?</h4>
                <p className="text-gray-700">
                  Check that crews were properly selected during scheduling. Refresh the planner if
                  split indicators aren't showing.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-red-600">Can't assign rower to crew?</h4>
                <p className="text-gray-700">
                  Verify the rower isn't already assigned to another crew in the same session. Check
                  availability and capacity limits.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-red-600">Coach double-booking warnings?</h4>
                <p className="text-gray-700">
                  Review the conflict details and confirm if intentional. Use the acknowledgment
                  checkboxes to proceed with known conflicts.
                </p>
              </div>
            </div>
          </section>

          {/* Quick Reference */}
          <section className="rounded-lg bg-gray-50 p-4">
            <h3 className="mb-4 text-xl font-semibold text-gray-900">📚 Quick Reference</h3>
            <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
              <div>
                <h4 className="mb-2 font-semibold">Actions:</h4>
                <ul className="space-y-1">
                  <li>• Drag session → Drop on time slot</li>
                  <li>• Configure crews and rowers</li>
                  <li>• Assign coaches</li>
                  <li>• Review and confirm</li>
                  <li>• Split sessions by crew selection</li>
                  <li>• Acknowledge warnings to proceed</li>
                </ul>
              </div>
              <div>
                <h4 className="mb-2 font-semibold">Indicators:</h4>
                <ul className="space-y-1">
                  <li>• 🟢 Preferred Times (Emerald)</li>
                  <li>• 🔵 Available Times (Blue/Teal)</li>
                  <li>• ⚪ Not Available (Gray)</li>
                  <li>• 🔴 Portside Preference</li>
                  <li>• 🟢 Starboard Preference</li>
                  <li>• ⚪ No Side Preference</li>
                  <li>• 🟠 Split Session Badge</li>
                  <li>• 📊 Progress Counter (X/Y crews)</li>
                </ul>
              </div>
              <div>
                <h4 className="mb-2 font-semibold">Session Splitting:</h4>
                <ul className="space-y-1">
                  <li>• Create multi-crew unscheduled sessions</li>
                  <li>• Schedule subset of crews at specific times</li>
                  <li>• Original session maintains remaining crews</li>
                  <li>• Repeat process until all crews scheduled</li>
                  <li>• Track progress with split indicators</li>
                </ul>
              </div>
              <div>
                <h4 className="mb-2 font-semibold">Modal Validation:</h4>
                <ul className="space-y-1">
                  <li>• 🔴 Errors must be resolved</li>
                  <li>• 🟡 Warnings can be acknowledged</li>
                  <li>• 🔵 Information provides suggestions</li>
                  <li>• Side preference validation</li>
                  <li>• Coach conflict management</li>
                  <li>• Capacity requirement checking</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export const AutoplannerHelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white">
        <div className="sticky top-0 flex items-center justify-between border-b bg-white p-6">
          <h2 className="text-2xl font-bold text-gray-900">Autoplanner</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="space-y-6 p-6 text-gray-700">
          <p>
            The Autoplanner creates schedule options for existing sessions in a training group. It
            walks you through selecting the group and sessions, validating availability, optionally
            locking seatings, choosing an optimization goal, and finally creating a task that
            generates results.
          </p>

          <ol className="list-inside list-decimal space-y-3">
            <li>
              <strong>Select Training Group</strong>
              <div className="text-sm text-gray-600">
                Pick the training group you want to plan for. This filters crews, sessions, and
                availability used in later steps.
              </div>
            </li>
            <li>
              <strong>Select Sessions and Date Range</strong>
              <div className="text-sm text-gray-600">
                Choose unscheduled sessions within a date range. If a day has multiple sessions,
                you’ll be asked to set their order. A planning task is created here and the session
                order is saved. You can optionally include scheduled sessions, but a warning will be
                shown first.
              </div>
            </li>
            <li>
              <strong>Validate Availability</strong>
              <div className="text-sm text-gray-600">
                The system analyzes availability and known constraints. Resolve flagged items by
                excluding sessions per crew or choosing resolutions per day. You can save as draft
                at any time; drafts preserve your exclusions and resolutions.
              </div>
            </li>
            <li>
              <strong>Locked Seatings (Optional)</strong>
              <div className="text-sm text-gray-600">
                For each crew, add setups and lock specific seatings for selected sessions. The
                system validates for conflicts (e.g., overlapping locks). This step may be skipped
                automatically when no experimental crews or locks are needed.
              </div>
            </li>
            <li>
              <strong>Choose Goal</strong>
              <div className="text-sm text-gray-600">
                Select the optimization goal the AI should use, for example: keep crews consistent,
                mix crews for variety, respect preferred availability, consistent times per crew,
                keep coach-crew pairings consistent, or rotate coaches.
              </div>
            </li>
            <li>
              <strong>Confirm Task</strong>
              <div className="text-sm text-gray-600">
                Review a summary of your configuration. When you create the task, it’s queued for
                processing and you’ll see a progress view. You’ll be notified when results are
                ready.
              </div>
            </li>
            <li>
              <strong>Processing and Results</strong>
              <div className="text-sm text-gray-600">
                The processing step tracks progress and then shows the generated options. From the
                Autoplanner dashboard you can open any task to view, compare, and apply results.
              </div>
            </li>
          </ol>

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
            Tips:
            <ul className="mt-1 list-inside list-disc space-y-1">
              <li>Use the date range to narrow the planning window</li>
              <li>Set session order when multiple sessions occur on the same day</li>
              <li>Save a draft during validation to continue later</li>
              <li>Only lock seatings you truly want to enforce; leave others flexible</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TrainingGroupsHelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white">
        <div className="sticky top-0 flex items-center justify-between border-b bg-white p-6">
          <h2 className="text-2xl font-bold text-gray-900">Training Groups</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="space-y-6 p-6 text-gray-700">
          <section>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Overview</h3>
            <p>
              Organize rowers and equipment into groups that underpin crew formation and planning.
              Manage members, assign coaches, and track equipment per group.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Layout</h3>
            <ul className="list-inside list-disc space-y-1">
              <li>Left: groups list with search; Right: selected group details</li>
              <li>Members: add rowers/coaches; roles; remove or transfer</li>
              <li>Equipment: assign boats and gear; track utilization</li>
            </ul>
          </section>

          <section>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Selections Workflow</h3>
            <ul className="list-inside list-disc space-y-1">
              <li>Tag members who are on trial or recovering</li>
              <li>Keep coach-to-rower ratio visible to plan evaluation sessions</li>
              <li>Use notes to capture selection criteria and outcomes</li>
            </ul>
          </section>

          <section>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Tips</h3>
            <ul className="list-inside list-disc space-y-1">
              <li>Group by squad/age/boat class for simpler filtering in Planner</li>
              <li>Assign primary coaches to each group for clearer responsibilities</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export const CrewsHelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white">
        <div className="sticky top-0 flex items-center justify-between border-b bg-white p-6">
          <h2 className="text-2xl font-bold text-gray-900">Crews</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="space-y-6 p-6 text-gray-700">
          <section>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Overview</h3>
            <p>
              Create and manage crew lineups. Choose boat type, mark crews experimental if needed,
              and drag-and-drop rowers into seats. Status indicators help you see completeness.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Interface</h3>
            <ul className="list-inside list-disc space-y-1">
              <li>Three-panel layout: crews list, lineup editor, available rowers</li>
              <li>Seat validation and constraints (cox required for coxed boats)</li>
              <li>Duplicate or clear lineups as needed</li>
            </ul>
          </section>

          <section>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              Selections & Temporary Crews
            </h3>
            <ul className="list-inside list-disc space-y-1">
              <li>
                Mark tryout lineups as <span className="font-medium">experimental</span>
              </li>
              <li>Iterate seats between sessions; record notes for decisions</li>
              <li>Convert experimental to standard once selections finish</li>
              <li>Create temporary crews for absences; archive when no longer needed</li>
            </ul>
          </section>

          <section>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Best Practices</h3>
            <ul className="list-inside list-disc space-y-1">
              <li>Use consistent naming (e.g., W8+ A, M4x Dev) for filtering and reports</li>
              <li>Balance sweep boats by side; respect preferences where practical</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};
